'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, clearSuperAdminTenantPersistence, type TenantOption } from '@/lib/api';
import { signIn, signOut, useSession } from 'next-auth/react';

interface User {
  id: number;
  email: string;
  nama?: string;
  noTelepon?: string;
  alamat?: string;
  role?: string | number;
  roleNama?: string;
  googleId?: string | null;
  profilePicture?: string | null;
  emailVerified?: boolean | string | null;
  perusahaanId?: number;
  cabangId?: number;
  namaPerusahaan?: string;
  namaCabang?: string;
  statusPlatform?: string;
  platformExpiredAt?: string | null;
  platformGraceUntil?: string | null;
  trialEndedAt?: string | null;
  platformLimitedUntil?: string | null;
  platformSuspendedUntil?: string | null;
  terminatedAt?: string | null;
  onboardingCompleted?: boolean | null;
  createdAt?: string;
  updatedAt?: string;
  permissions?: string[];
  // 2FA Google Authenticator (TOTP)
  totpEnabled?: boolean;
  mfaVerified?: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  // True bila user terautentikasi sebagai SuperAdmin (pemilik aplikasi, tanpa perusahaan).
  isSuperAdmin: boolean;
  // True bila user terautentikasi tetapi belum memilih perusahaan aktif (multi-tenant).
  needsTenantSelection: boolean;
  // True bila user wajib menyelesaikan onboarding (Owner baru); SuperAdmin dikecualikan.
  needsOnboarding: boolean;
  // True bila user ber-2FA belum melewati gate kode pada sesi ini.
  needsOtpVerification: boolean;
  // True bila email user belum terverifikasi → wajib isi OTP (kecuali SuperAdmin & login Google).
  needsEmailVerification: boolean;
  // Ambil ulang profil dari /auth/me (mis. setelah verifikasi 2FA / enroll).
  refreshUser: () => Promise<void>;
  // Halaman "home" sesuai peran (SuperAdmin: /perusahaan, lainnya: /dashboard).
  homePath: string;
  googleAuthError: string | null;
  clearGoogleAuthError: () => void;
  login: (email: string, password: string) => Promise<{ requiresTenantSelection: boolean }>;
  loginWithGoogle: () => Promise<void>;
  // Memilih / berpindah perusahaan aktif; mengembalikan user terbaru.
  selectTenant: (perusahaanId: number) => Promise<User>;
  // Daftar perusahaan (membership aktif) milik user.
  loadMyPerusahaan: () => Promise<TenantOption[]>;
  register: (data: {
    namaBengkel: string;
    nama: string;
    email: string;
    password: string;
    noTelepon: string;
    alamat?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  hasPermission: (code: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [googleAuthError, setGoogleAuthError] = useState<string | null>(null);
  const { data: session, status } = useSession();

  const isAuthenticated = !!user;
  const isSuperAdmin = user?.roleNama === 'SuperAdmin';
  // Authenticated tapi belum punya perusahaan terpilih → wajib pilih tenant.
  // SuperAdmin (pemilik aplikasi) bersifat global tanpa perusahaan → tidak perlu pilih tenant.
  const needsTenantSelection =
    isAuthenticated && !user?.perusahaanId && !isSuperAdmin;
  // SuperAdmin global tidak punya perusahaan untuk di-onboarding.
  const needsOnboarding =
    isAuthenticated && user?.onboardingCompleted === false && !isSuperAdmin;
  // Gate 2FA: user (role apa pun) yang mengaktifkan TOTP tapi sesi belum terverifikasi.
  const needsOtpVerification =
    isAuthenticated && !!user?.totpEnabled && !user?.mfaVerified;
  // Gate verifikasi email: SEMUA user wajib verifikasi email lewat OTP bila
  // emailVerified belum true — termasuk SuperAdmin dan login Google.
  const needsEmailVerification =
    isAuthenticated && user?.emailVerified !== true;
  // Halaman "home" sesuai peran (SuperAdmin: kelola perusahaan lintas-tenant).
  const homePath = isSuperAdmin ? '/perusahaan' : '/dashboard';

  // Restore user state via /api/auth/me — otentikasi pakai HttpOnly cookie.
  // Tidak ada data sensitif yang dipersist di browser storage.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await authApi.me();
        if (!cancelled && response.status && response.data) {
          setUser(response.data as User);
        }
      } catch {
        // 401 / cookie invalid → user tetap null
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Google OAuth exchange — hanya jalan saat status berubah jadi 'authenticated'
  // dan user belum terisi (belum exchange ke backend).
  useEffect(() => {
    if (status !== 'authenticated' || !session?.user || user) return;

    let cancelled = false;
    const idToken = (session as any).idToken;
    if (!idToken) return;

    (async () => {
      try {
        const response = await authApi.googleAuth(idToken);
        if (!cancelled && response.status && response.data) {
          setUser(response.data as User);
        }
      } catch (err: any) {
        if (!cancelled) {
          const raw: string = err?.message || 'Google login gagal. Silakan coba lagi.';
          setGoogleAuthError(raw.replace(/^\d{3}\s/, ''));
        }
        await signOut({ redirect: false });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email: string, password: string): Promise<{ requiresTenantSelection: boolean }> => {
    try {
      setIsLoading(true);
      const response = await authApi.login({ email, password });
      if (!response.status) {
        throw new Error(response.message || 'Login failed');
      }
      const data: any = response.data;
      // Multi-perusahaan: backend minta pemilihan tenant — jangan set user dulu.
      if (data?.requires_tenant_selection) {
        setUser(null);
        return { requiresTenantSelection: true };
      }
      if (data) {
        setUser(data as User);
      }
      return { requiresTenantSelection: false };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const selectTenant = async (perusahaanId: number): Promise<User> => {
    const response = await authApi.selectTenant(perusahaanId);
    if (!response.status || !response.data) {
      throw new Error(response.message || 'Gagal memilih perusahaan');
    }
    const data = response.data as User;
    setUser(data);
    return data;
  };

  const loadMyPerusahaan = async (): Promise<TenantOption[]> => {
    const response = await authApi.myPerusahaan();
    return (response.data as TenantOption[]) ?? [];
  };

  const loginWithGoogle = async () => {
    try {
      setIsLoading(true);
      const result = await signIn('google');
      if (result?.error) {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Google login error:', error);
      setIsLoading(false);
      throw error;
    }
  };

  const register = async (data: {
    namaBengkel: string;
    nama: string;
    email: string;
    password: string;
    noTelepon: string;
    alamat?: string;
  }) => {
    try {
      setIsLoading(true);
      const response = await authApi.register(data);
      if (!response.status) {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Logout error:', err);
    }

    if (session) {
      await signOut({ redirect: false });
    }

    setUser(null);
    clearSuperAdminTenantPersistence();
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  };

  const clearGoogleAuthError = () => setGoogleAuthError(null);

  // Patch in-memory user state — dipakai setelah update profil / perusahaan
  // supaya UI langsung reflek tanpa perlu round-trip ke /me.
  const updateUser = (updates: Partial<User>) => {
    setUser(prev => (prev ? { ...prev, ...updates } : prev));
  };

  const hasPermission = (code: string) => {
    if (!code) return true;
    if (isSuperAdmin) return true;
    return user?.permissions?.includes(code) ?? false;
  };

  // Re-fetch profil dari /me (cookie HttpOnly). Dipakai setelah verifikasi/enroll 2FA
  // agar flag totpEnabled/mfaVerified ter-update dan gate tertutup.
  const refreshUser = async () => {
    try {
      const response = await authApi.me();
      if (response.status && response.data) {
        setUser(response.data as User);
      }
    } catch {
      // abaikan — biarkan state lama
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    isSuperAdmin,
    needsTenantSelection,
    needsOnboarding,
    needsOtpVerification,
    needsEmailVerification,
    refreshUser,
    homePath,
    googleAuthError,
    clearGoogleAuthError,
    login,
    loginWithGoogle,
    selectTenant,
    loadMyPerusahaan,
    register,
    logout,
    updateUser,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
